import {
  calculateSurvivabilityNomogram,
  calculatePSAProgressionFreeSurvivalNomogram,
  calculateLogisticPSA50PercentDeclilneNomogram,
} from "./predictionModel";
import pdfMake from "pdfmake";

pdfMake.fonts = {
  interstate: {
    normal:
      "https://use.typekit.net/af/20ad3a/00000000000000003b9ae7f7/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3",
    bold: "https://use.typekit.net/af/630ec8/00000000000000003b9ae7f8/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3",
    italics:
      "https://use.typekit.net/af/b7ea2b/00000000000000003b9ae7f5/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3",
    bolditalics:
      "https://use.typekit.net/af/f67d01/00000000000000003b9ae7f9/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3",
  },
  Roboto: {
    normal: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf",
    bold: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf",
    italics: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf",
    bolditalics: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf",
  },
};

function horizontalLine() {
  return {
    canvas: [{ type: "line", x1: 0, y1: 5, x2: 595 - 2 * 40, y2: 5, lineWidth: 1 }],
    margin: [0, 0, 0, 12],
  };
}

function riskColor(lowRisk) {
  return lowRisk ? "blue" : "red";
}

function riskText(lowRisk) {
  return lowRisk ? "Low" : "High";
}

function generateReport(inputs, predictions, chartImages) {
  const survivabilityNomogram = calculateSurvivabilityNomogram(inputs);
  const psaProgressionFreeSurvivalNomogram = calculatePSAProgressionFreeSurvivalNomogram(inputs);
  const logisticPSA50PercentDeclineNomogram = calculateLogisticPSA50PercentDeclilneNomogram(inputs);

  const noBorder = {
    fillColor: "#F2F2F2",
    border: [false, false, false, false],
  };
  const topBorder = {
    fillColor: "#F2F2F2",
    border: [false, true, false, false],
  };
  const bottomBorder = {
    fillColor: "#F2F2F2",
    border: [false, false, false, true],
  };

  const reportDefinition = {
    info: {
      title: "LuPSMA Report",
    },
    content: [
      // 1st page
      {
        text: "Prognosis model for outcome after LuPSMA therapy in patients with metastatic \ncastration-resistant prostate cancer",
        style: ["title", "blue"],
        margin: [0, 0, 0, 18],
      },
      {
        text: "SYNOPSIS",
        style: ["header", "blue"],
      },
      horizontalLine(),
      {
        text: "This model was developed to predict outcome after LuPSMA treatment in men with metastatic castration-resistant prostate cancer. It predicts the risk of survival at 12 and 18 months and risk of PSA progression-free at 3 and 6 months after treatment initiation. These personalized estimates may help physicians who are considering treating patients with LuPSMA radionuclide therapy – but they cannot substitute clinical judgment and dictate clinical decision. The tool can also help for designing clinical trials. The model has been developed using data of 196 patients from three institutions and validated using 74 patients from the other three institutions.",
        margin: [0, 0, 0, 12],
      },
      {
        text: "PERSONALIZED PROGNOSIS",
        style: ["header", "blue"],
      },
      horizontalLine(),
      {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: "Overall Survival",
                bold: true,
                alignment: "center",
                lineHeight: 1.5,
              },
              {
                text: [
                  "Risk Group: ",
                  {
                    text: riskText(survivabilityNomogram.lowRisk),
                    style: riskColor(survivabilityNomogram.lowRisk),
                    bold: true,
                  },
                ],
                lineHeight: 1.5,
              },
              {
                text: [
                  "Estimated at 12 and 18 months: ",
                  {
                    text: `${Math.round(predictions.survivability12Months * 100)}`,
                    style: riskColor(survivabilityNomogram.lowRisk),
                    bold: true,
                  },
                  "% and ",
                  {
                    text: `${Math.round(predictions.survivability18Months * 100)}`,
                    style: riskColor(survivabilityNomogram.lowRisk),
                    bold: true,
                  },
                  "%",
                ],
                lineHeight: 1.5,
              },
              {
                image: chartImages.survivabilityChart,
                width: "250",
              },
            ],
          },
          {
            width: "50%",
            stack: [
              {
                text: "PSA Progression-free Survival",
                bold: true,
                alignment: "center",
                lineHeight: 1.5,
              },
              {
                text: [
                  "Risk Group: ",
                  {
                    text: riskText(psaProgressionFreeSurvivalNomogram.lowRisk),
                    style: riskColor(psaProgressionFreeSurvivalNomogram.lowRisk),
                    bold: true,
                  },
                ],
                lineHeight: 1.5,
              },
              {
                text: [
                  "Estimated at 3 and 6 months: ",
                  {
                    text: `${Math.round(predictions.psaProgressionFreeSurvival3Months * 100)}`,
                    style: riskColor(psaProgressionFreeSurvivalNomogram.lowRisk),
                    bold: true,
                  },
                  "% and ",
                  {
                    text: `${Math.round(predictions.psaProgressionFreeSurvival6Months * 100)}`,
                    style: riskColor(psaProgressionFreeSurvivalNomogram.lowRisk),
                    bold: true,
                  },
                  "%",
                ],
                lineHeight: 1.5,
              },
              {
                image: chartImages.psaProgressionFreeChart,
                width: "250",
              },
            ],
          },
        ],
        columnGap: 10,
      },
      {
        text: [
          "PSA Response Probability*  ",
          {
            text: `${Math.round(predictions.logisticPSA50PercentDecline * 100)}`,
            style: riskColor(logisticPSA50PercentDeclineNomogram.lowRisk),
          },
          "%",
        ],
        bold: true,
        alignment: "center",
        lineHeight: 1.5,
        margin: [0, 8, 0, 0],
      },
      logisticPSA50PercentDeclineNomogram.lowRisk
        ? {}
        : {
            text: "Likely non-responder",
            style: riskColor(logisticPSA50PercentDeclineNomogram.lowRisk),
            bold: true,
            alignment: "center",
            lineHeight: 1.5,
          },
      { text: "The results were obtained based on the following calculations:", bold: true, margin: [0, 4, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ["*", "auto", 80, 80, 80],
          body: [
            [
              { ...bottomBorder, text: "Parameter", bold: true, alignment: "left" },
              { ...bottomBorder, text: "Value", bold: true, alignment: "center" },
              { ...bottomBorder, text: "Points for OS", bold: true, alignment: "center" },
              { ...bottomBorder, text: "Points for PSA-PFS", bold: true, alignment: "center" },
              { ...bottomBorder, text: "Points for PSA decline", bold: true, alignment: "center" },
            ],
            [
              { ...noBorder, text: "Time since prostate cancer diagnosis", alignment: "left" },
              { ...noBorder, text: `${inputs.timeSinceDiagnosis} years`, alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.timeSinceDiagnosis, alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.timeSinceDiagnosis, alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
            ],
            [
              { ...noBorder, text: "Previous chemotherapy", alignment: "left" },
              { ...noBorder, text: inputs.previousChemotheraphy ? "Yes" : "No", alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.previousChemotheraphy, alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.previousChemotheraphy, alignment: "center" },
              { ...noBorder, text: logisticPSA50PercentDeclineNomogram.previousChemotheraphy, alignment: "center" },
            ],
            [
              { ...noBorder, text: "Hemoglobin level", alignment: "left" },
              { ...noBorder, text: `${inputs.hemoglobin} g/dl`, alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.hemoglobin, alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
            ],
            [
              { ...noBorder, text: "Tumor PSMA uptake (SUVmean)", alignment: "left" },
              { ...noBorder, text: inputs.suvMean, alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.suvMean, alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.suvMean, alignment: "center" },
              { ...noBorder, text: logisticPSA50PercentDeclineNomogram.suvMean, alignment: "center" },
            ],
            [
              { ...noBorder, text: "Number of metastases", alignment: "left" },
              {
                ...noBorder,
                text: inputs.metastasesCount
                  ? {
                      text: [
                        {
                          text: "≥",
                          font: "Roboto",
                        },
                        "\u00A020",
                      ],
                    }
                  : "<\u00A020",
                alignment: "center",
              },
              { ...noBorder, text: survivabilityNomogram.metastasesCount, alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
            ],
            [
              { ...noBorder, text: "Pelvic lymph node metastases", alignment: "left" },
              { ...noBorder, text: inputs.pelvicLymphNodes ? "Yes" : "No", alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.pelvicLymphNodes, alignment: "center" },
              { ...noBorder, text: logisticPSA50PercentDeclineNomogram.pelvicLymphNodes, alignment: "center" },
            ],
            [
              { ...noBorder, text: "Bone metastases", alignment: "left" },
              { ...noBorder, text: inputs.boneMetastases ? "Yes" : "No", alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.boneMetastases, alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.boneMetastases, alignment: "center" },
              { ...noBorder, text: "–", alignment: "center" },
            ],
            [
              { ...noBorder, text: "Liver metastases", alignment: "left" },
              { ...noBorder, text: inputs.liverMetastases ? "Yes" : "No", alignment: "center" },
              { ...noBorder, text: survivabilityNomogram.liverMetastases, alignment: "center" },
              { ...noBorder, text: psaProgressionFreeSurvivalNomogram.liverMetastases, alignment: "center" },
              { ...noBorder, text: logisticPSA50PercentDeclineNomogram.liverMetastases, alignment: "center" },
            ],
            [
              { ...topBorder, text: "Total", bold: true, alignment: "left" },
              { ...topBorder, text: "" },
              { ...topBorder, text: survivabilityNomogram.pointsTotal, bold: true, alignment: "center" },
              { ...topBorder, text: psaProgressionFreeSurvivalNomogram.pointsTotal, bold: true, alignment: "center" },
              { ...topBorder, text: logisticPSA50PercentDeclineNomogram.pointsTotal, bold: true, alignment: "center" },
            ],
          ],
        },
      },
      {
        text: [
          { text: "Reference:", bold: true, decoration: "underline", fontSize: 8 },
          { text: " " },
          {
            text: 'Gafita, Andrei, Jeremie Calais, Tristan R. Grogan, Boris Hadaschik, Hui Wang, Manuel Weber, Shahneen Sandhu et al. "Nomograms to predict outcomes after 177Lu-PSMA therapy in men with metastatic castration-resistant prostate cancer: an international, multicentre, retrospective study." The Lancet Oncology (2021).',
            link: "https://www.thelancet.com/journals/lanonc/article/PIIS1470-2045(21)00274-6/fulltext",
            decoration: "underline",
            fontSize: 8,
            color: "#003B5C",
          },
        ],
        margin: [0, 6],
      },
      {
        text: "* PSA response was defined as a reduction of 50% or more from baseline at any time during treatment.",
        fontSize: 8,
        pageBreak: "after",
      },
      // 2nd page
      {
        text: "PRETHERAPEUTIC CHARACTERISTICS",
        style: ["header", "blue"],
      },
      horizontalLine(),
      {
        text: [
          { text: "Time since initial diagnosis of prostate cancer: ", style: "blue" },
          { text: `${inputs.timeSinceDiagnosis} years.`, bold: true },
        ],
      },
      {
        text: "A longer time since initial diagnosis is associated with better prognosis. The median time since initial diagnosis observed in the international study was 6.6 years.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Previous chemotherapy: ", style: "blue" },
          { text: inputs.previousChemotheraphy ? "Yes." : "No.", bold: true },
        ],
      },
      {
        text: inputs.previousChemotheraphy
          ? "Previous treatment with chemotherapy has a negative impact on the prognosis."
          : "Patients who had not been treated previously with chemotherapy have a better prognosis.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Hemoglobin levels: ", style: "blue" },
          { text: `${inputs.hemoglobin} g/dl.`, bold: true },
        ],
      },
      {
        text: `Higher levels of hemoglobin are associated with better prognosis. The average hemoglobin level in the study was 11.3 g/dl.`,
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Number of metastases: ", style: "blue" },
          inputs.metastasesCount
            ? {
                text: [
                  {
                    text: "≥",
                    font: "Roboto",
                    bold: true,
                  },
                  {
                    text: "\u00A020",
                    bold: true,
                  },
                ],
              }
            : {
                text: "<\u00A020",
                bold: true,
              },
        ],
      },
      {
        text: inputs.metastasesCount
          ? "Higher total number of metastases is associated with worse prognosis."
          : "Lower metastases count is associated with better prognosis.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Pelvic lymph node metastases: ", style: "blue" },
          { text: inputs.pelvicLymphNodes ? "Yes." : "No.", bold: true },
        ],
      },
      {
        text: inputs.pelvicLymphNodes
          ? "The presence of local lymph node metastases is associated with longer time to PSA progression."
          : "Patients of whom prostate cancer has not spread to the pelvic lymph nodes have a longer time to PSA progression.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Bone metastases: ", style: "blue" },
          { text: inputs.boneMetastases ? "Yes." : "No.", bold: true },
        ],
      },
      {
        text: inputs.boneMetastases
          ? "The presence of bone metastases is associated with worse prognosis."
          : "Patients of whom prostate cancer has not spread to the bone have a better prognosis.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Liver metastases: ", style: "blue" },
          { text: inputs.liverMetastases ? "Yes." : "No.", bold: true },
        ],
      },
      {
        text: inputs.liverMetastases
          ? "The presence of liver metastases is associated with worse prognosis."
          : "Patients of whom prostate cancer has not spread to the liver have a better prognosis.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Total tumor PSMA uptake: ", style: "blue" },
          {
            text: `SUVmean ${inputs.suvMean}.`,
            bold: true,
          },
        ],
      },
      {
        text: "A higher tumor PSMA uptake is associated with higher probability of response to LuPSMA treatment. The average SUVmean observed in the international study was 8.5.",
        italics: true,
        margin: [0, 0, 0, 12],
      },
      {
        text: [
          { text: "Total Number of Points: ", style: "blue" },
          { text: `${survivabilityNomogram.pointsTotal} for Overall Survival, `, bold: true },
          { text: `${psaProgressionFreeSurvivalNomogram.pointsTotal} for PSA progression-free survival`, bold: true },
          { text: " and " },
          {
            text: `${logisticPSA50PercentDeclineNomogram.pointsTotal} for the probability of maximum PSA decline >\u00A050%.`,
            bold: true,
          },
        ],
      },
      {
        text: "A greater number of points for overall survival and PSA progression-free survival is associated with higher risk of death and PSA progression. Patients with <\u00A0197 points for overall survival and <\u00A0178 points for PSA progression-free survival are stratified into the low risk group. According to the prediction model, patients from the low-risk group have a higher probability to benefit from LuPSMA treatment.",
        italics: true,
        margin: [0, 0, 0, 4],
      },
      {
        text: 'Patients with <\u00A041 points in the PSA decline model are unlikely to achieve a PSA decline >\u00A050%. They are classified as "likely non-responders".',
        italics: true,
        margin: [0, 0, 0, 12],
      },
    ],
    styles: {
      title: {
        fontSize: 12,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 12],
      },
      header: {
        fontSize: 11,
        bold: true,
      },
      blue: {
        // UCLA Darker Blue
        color: "#005587",
      },
      yellow: {
        // UCLA Darkest Gold
        color: "#FFB81C",
      },
      red: {
        color: "#960005",
      },
    },
    defaultStyle: {
      fontSize: 11,
      alignment: "justify",
      font: "interstate",
    },
  };

  return pdfMake.createPdf(reportDefinition);
}

export default generateReport;
